import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ApiClient, LueftungBauteil, LueftungBauteilDetail } from '../../api';
import { Legende } from '../../Farbregel';
import {
  ContentSection, DataTable, ErrorState, LoadingState, PropertyGrid, StatusBadge, Vorbehalte, formatiereZahl,
  spalteFuer, type Spalte, type Vorbehalt,
} from '../../muster';
import { bauteilAnfrage, nurGeaenderte, werteAus, type Traeger } from './bestandregeln';
import { tag, zeitpunkt } from './pruefung';
import { Herstellerunterlagen } from './LueftungDatenblatt';
import { Feldgruppen } from './LueftungFeld';

/** Die Grenze der Bauteilabfrage im Server - mehr kommen nicht. */
export const BAUTEILGRENZE = 1000;

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE BAUTEILE EINES TRÄGERS (TV6c)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Gruppiert nach Luftart und Bauart - eine Brandschutzklappe im Zuluftnetz ist
 * ein anderes Bauteil als eine im Abluftnetz derselben Anlage. Seit TV6c eine
 * `DataTable` mit Gruppenzeilen statt einer rohen Tabelle je Gruppe; die
 * Zeile öffnet die Datenpflege (`beiZeileKlick`, mit Tastatur), der Verweis
 * „Produktseite“ ist ein eigener Link.
 */
export function Bauteilliste({ api, projektId, traeger, oeffnen, zurAnlage }: {
  api: ApiClient;
  projektId: string;
  traeger: Traeger;
  oeffnen: (id: string) => void;
  /** Zurück zu den Stammdaten der Anlage. Nur bei einer Anlage gesetzt. */
  zurAnlage?: () => void;
}) {
  const [bauteile, setBauteile] = useState<LueftungBauteil[] | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);

  const laden = useCallback(() => {
    setBauteile(null);
    setFehler(null);
    api.lueftungBauteile(projektId, bauteilAnfrage(traeger)).then(setBauteile).catch(setFehler);
    // Die Kennung genügt: Der Name ändert die Abfrage nicht.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, projektId, traeger.art, traeger.id]);

  useEffect(() => { laden(); }, [laden]);

  const spalten = useMemo<Spalte<LueftungBauteil>[]>(() => [
    {
      schluessel: 'aks', titel: 'AKS', immerSichtbar: true, wert: (b) => b.aksSchluessel ?? '',
      inhalt: (b) => b.aksSchluessel ?? <StatusBadge zustand="neutral" text="nicht vergeben" klein />,
    },
    { schluessel: 'typ', titel: 'Typ', wert: (b) => b.typ ?? '' },
    {
      schluessel: 'hersteller', titel: 'Hersteller / Produkt',
      wert: (b) => [b.hersteller, b.produktcode && b.produktcode !== b.hersteller ? b.produktcode : null].filter(Boolean).join(' · '),
    },
    { schluessel: 'groesse', titel: 'Größe', wert: (b) => b.groesse ?? '' },
    { schluessel: 'volumenstrom', titel: 'Volumenstrom', ...spalteFuer('volumenstrom_luft'), wert: (b) => b.volumenstrom },
    {
      schluessel: 'raum', titel: 'Raum',
      wert: (b) => (b.raumnummer ? `${b.raumnummer}${b.raumname ? ` · ${b.raumname}` : ''}` : 'kein Raum'),
    },
    {
      // Zugewiesen heißt: Werte aus einem Datenblatt liegen vor; interpoliert ist eine begründete Annahme.
      schluessel: 'pruefung', titel: 'Prüfung', art: 'status',
      wert: (b) => pruefmarken(b).map((m) => m.text).join(', '),
      inhalt: (b) => <>{pruefmarken(b).map((m) => <StatusBadge key={m.text} {...m} klein />)}</>,
    },
    {
      schluessel: 'produktseite', titel: 'Produktseite', sortierbar: false, wert: () => '',
      // Eigener Link und nicht der Zeilenklick: Der öffnet die Datenpflege.
      inhalt: (b) => (
        <Link to={`/projekte/${projektId}/lueftung/komponente/${b.id}`} onClick={(e) => e.stopPropagation()}>
          Produktseite
        </Link>
      ),
    },
  ], [projektId]);

  return (
    <ContentSection
      titel={traeger.name}
      beschreibung="Bauteile nach Luftart und Bauart. Eine Zeile wählen öffnet die Datenpflege des Bauteils."
      aktionen={zurAnlage && <button type="button" className="m-knopf" onClick={zurAnlage}>Anlagendaten</button>}
    >
      <DataTable<LueftungBauteil>
        name="Bauteile"
        spalten={spalten}
        zeilen={bauteile ?? []}
        schluessel={(b) => b.id}
        laedt={bauteile === null && fehler === null}
        fehler={fehler ?? undefined}
        beiErneutVersuchen={laden}
        gruppierung={{ nach: (b) => `${b.luftart ?? 'ohne Luftart'} · ${b.bauart ?? b.familie ?? '—'}`, ordnung: 'eingang' }}
        beiZeileKlick={(b) => oeffnen(b.id)}
        zeilenKlasse={(b) => (b.datenblattId ? 'zugewiesen' : undefined)}
        trefferText={bauteile && bauteile.length >= BAUTEILGRENZE
          ? `${formatiereZahl(BAUTEILGRENZE, 0)} Bauteile - die Abfrage endet dort; für den Rest einen kleineren Träger wählen`
          : undefined}
        suchePlatzhalter="AKS, Typ, Raum …"
        ausfuhr
        ausfuhrDateiname={`lueftung-bauteile-${traeger.name}.csv`}
        dicht
        leer={<span>Dieser Träger hat keine Bauteile.</span>}
      />
    </ContentSection>
  );
}

/** Die Marken der Spalte „Prüfung“ - je Aussage ein Wort (vorher `.marke` mit Tooltip). */
export function pruefmarken(b: LueftungBauteil) {
  const m: { zustand: 'gut' | 'warnung' | 'neutral'; text: string }[] = [];
  if (b.datenblattId) m.push({ zustand: 'gut', text: b.abgleichArt === 'interpoliert' ? 'zugewiesen, interpoliert' : 'zugewiesen' });
  if (b.hatFehler) m.push({ zustand: 'warnung', text: 'Fehler' });
  if (b.modellpruefung && b.modellpruefung !== 'OK') m.push({ zustand: 'warnung', text: b.modellpruefung });
  // Die Menge hat sich seit dem Abgleich geändert - der Wert am Bauteil gilt für die alte.
  if (b.datenblattId && b.abgleichMenge !== null && b.volumenstrom !== null && b.abgleichMenge !== b.volumenstrom) {
    m.push({ zustand: 'warnung', text: `Menge geändert (abgeglichen für ${formatiereZahl(b.abgleichMenge, 0)} m³/h)` });
  }
  return m;
}

// ═══ Ein Bauteil bearbeiten ══════════════════════════════════════════════════════
//
// Der Feldkatalog kommt vom Server und ist auf die Bauart eingeschränkt (eine
// Brandschutzklappe 49 Felder, ein Tellerventil 31). Modellwerte oben,
// unveränderlich: Was aus Revit kommt, wird dort gepflegt.
//
// Schreibweg (TVX3): Die Seite schickt nur die geänderten Felder, als Text, an
// `lueftungSpeichern` (`nurGeaenderte`); der Server schreibt nur, was sich ändert,
// und setzt nur dafür die Herkunft „hand“. Bis TVX3 gingen ALLE Werte zurück.

export function Bauteilseite({ api, bauteilId, projektId, zurueck }: {
  api: ApiClient;
  bauteilId: string;
  projektId: string;
  zurueck: () => void;
}) {
  const [bauteil, setBauteil] = useState<LueftungBauteilDetail | null>(null);
  const [werte, setWerte] = useState<Record<string, string>>({});
  const [herkunft, setHerkunft] = useState<Record<string, string>>({});
  const [geaendert, setGeaendert] = useState(false);
  const [geaenderteFelder, setGeaenderteFelder] = useState<string[]>([]);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [ladefehler, setLadefehler] = useState<unknown>(null);
  const [gesichert, setGesichert] = useState(false);

  const laden = useCallback(() => {
    setBauteil(null);
    setGeaendert(false);
    setGeaenderteFelder([]);
    setGesichert(false);
    setFehler(null);
    setLadefehler(null);
    api.lueftungBauteil(bauteilId)
      .then((b) => {
        setBauteil(b);
        setWerte(werteAus(b.dataJson));
        setHerkunft(werteAus(b.herkunft));
      })
      .catch(setLadefehler);
  }, [api, bauteilId]);

  useEffect(() => { laden(); }, [laden]);

  const sichern = async () => {
    setLaeuft(true);
    setFehler(null);
    try {
      await api.lueftungSpeichern(bauteilId, nurGeaenderte(werte, geaenderteFelder));
      setGeaendert(false);
      setGeaenderteFelder([]);
      setGesichert(true);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  const setzen = (schluessel: string, wert: string) => {
    setWerte((w) => ({ ...w, [schluessel]: wert }));
    setGeaenderteFelder((g) => (g.includes(schluessel) ? g : [...g, schluessel]));
    setGeaendert(true);
    setGesichert(false);
  };

  const zurueckKnopf = <button type="button" className="m-knopf" onClick={zurueck}>zurück</button>;

  if (ladefehler !== null) {
    return (
      <ContentSection titel="Bauteil" aktionen={zurueckKnopf}>
        <ErrorState meldung="Das Bauteil konnte nicht abgerufen werden." technisch={ladefehler} beiErneutVersuchen={laden} />
      </ContentSection>
    );
  }
  if (!bauteil) {
    return (
      <ContentSection titel="Bauteil" aktionen={zurueckKnopf}>
        <LoadingState text="Bauteil wird abgerufen …" zeilen={6} />
      </ContentSection>
    );
  }

  const pflichtOffen = bauteil.felder.filter((f) => f.istPflicht && !werte[f.fieldKey]?.trim());
  const vorbehalte: Vorbehalt[] = [];
  if (bauteil.datenblattId && bauteil.abgleichMenge !== null && bauteil.volumenstrom !== null
      && bauteil.abgleichMenge !== bauteil.volumenstrom) {
    vorbehalte.push({
      art: 'veraltet',
      text: `Die Herstellerwerte sind für ${formatiereZahl(bauteil.abgleichMenge, 0)} m³/h abgeglichen; die Luftmenge ist `
        + `jetzt ${formatiereZahl(bauteil.volumenstrom, 0)} m³/h. Der Wert gilt für die alte Menge.`,
    });
  }
  if (pflichtOffen.length > 0) {
    vorbehalte.push({
      art: 'sonstiges',
      text: `Ohne Angabe: ${pflichtOffen.map((f) => f.label).join(', ')}. Für Abnahme und Datenpunktliste werden sie gebraucht.`,
    });
  }

  return (
    <>
      <ContentSection
        titel={bauteil.aksSchluessel ?? bauteil.typ ?? 'Bauteil'}
        beschreibung={[bauteil.bauart, bauteil.luftart].filter(Boolean).join(' · ') || undefined}
        aktionen={zurueckKnopf}
      >
        <Vorbehalte eintraege={vorbehalte} />
        {/* Was aus dem Modell kommt. Unveränderlich, weil es dort gepflegt wird. */}
        <PropertyGrid
          spalten={3}
          dicht
          eintraege={[
            { beschriftung: 'Anlage', wert: bauteil.anlage ?? '—', herkunft: 'modell' },
            { beschriftung: 'Kanalsystem', wert: bauteil.systemname ?? '—', herkunft: 'modell' },
            { beschriftung: 'Familie', wert: bauteil.familie ?? '—', herkunft: 'modell' },
            { beschriftung: 'Typ', wert: bauteil.typ ?? '—', herkunft: 'modell' },
            { beschriftung: 'Größe', wert: bauteil.groesse ?? '—', herkunft: 'modell' },
            // Hersteller und Produktcode kommen aus dem Datenblattimport - dieselbe Quelle wie in der Liste.
            { beschriftung: 'Hersteller', wert: bauteil.hersteller ?? '—', herkunft: 'daten' },
            { beschriftung: 'Produktcode', wert: bauteil.produktcode ?? '—', herkunft: 'daten' },
            { beschriftung: 'Volumenstrom', wert: bauteil.volumenstrom, einheit: 'm³/h', nachkomma: 0, herkunft: 'modell' },
            { beschriftung: 'Geschoss', wert: bauteil.ebene ?? '—', herkunft: 'modell' },
            {
              beschriftung: 'Raum',
              wert: bauteil.raumnummer ? `${bauteil.raumnummer}${bauteil.raumname ? ` · ${bauteil.raumname}` : ''}` : 'kein Raum getroffen',
              hinweis: bauteil.raumQuelle === 'geometrie' ? 'aus der Lage ermittelt' : undefined,
              herkunft: 'modell',
            },
            {
              beschriftung: 'Lage', herkunft: 'modell',
              wert: bauteil.posX !== null ? `${formatiereZahl(bauteil.posX, 0)} / ${formatiereZahl(bauteil.posY, 0)} / ${formatiereZahl(bauteil.posZ, 0)} mm` : '—',
            },
            { beschriftung: 'Kennung', wert: bauteil.elementGuid, herkunft: 'modell', breit: true },
          ]}
        />
        {bauteil.verbundanlagen.length > 1 && (
          <p className="lue-leise">
            Verbundnetz für {formatiereZahl(bauteil.verbundanlagen.length, 0)} Anlagen: {bauteil.verbundanlagen.join(', ')}
          </p>
        )}
        {bauteil.datenblattId && (
          <p className="lue-leise">
            <StatusBadge zustand="gut" text={bauteil.abgleichArt === 'interpoliert' ? 'Herstellerdaten zugewiesen, interpoliert' : 'Herstellerdaten zugewiesen'} klein />
            {bauteil.abgleichMenge !== null && <> abgeglichen für {formatiereZahl(bauteil.abgleichMenge, 0)} m³/h</>}
            {bauteil.abgleichAm && <> · {tag(bauteil.abgleichAm)}{bauteil.abgleichVon && <> von {bauteil.abgleichVon}</>}</>}
          </p>
        )}
      </ContentSection>

      {bauteil.hatFehler && <Befund api={api} bauteil={bauteil} neuLaden={laden} />}

      {bauteil.familie && bauteil.typ && (
        <Herstellerunterlagen
          api={api}
          familie={bauteil.familie}
          typ={bauteil.typ}
          bauart={bauteil.bauart}
          projektId={projektId}
          bauteilId={bauteilId}
          felder={bauteil.felder}
          datenblattUrl={werte.datenblatt_url ?? null}
        />
      )}

      <ContentSection titel="Werte pflegen" beschreibung="Die Felder der Bauart, nach Fachgruppe.">
        <Legende />
        <Feldgruppen felder={bauteil.felder} werte={werte} herkunft={herkunft} setzen={setzen} />
        {fehler && <ErrorState klein meldung={fehler} />}
        <div className="lue-speichern">
          <button type="button" className="m-knopf m-knopf-haupt" disabled={!geaendert || laeuft} onClick={() => void sichern()}>
            Speichern
          </button>
          <span className="lue-meldung" role="status">{gesichert ? 'gespeichert' : ''}</span>
          {bauteil.updatedBy && <span className="lue-leise">zuletzt: {bauteil.updatedBy}</span>}
        </div>
      </ContentSection>
    </>
  );
}

// ═══ Befund aus dem Datenblattabgleich ════════════════════════════════════════════
//
// Die geplante Luftmenge liegt außerhalb der Herstellertabelle - ein
// Planungsbefund, kein Datenproblem. Er steht deshalb oben.

function Befund({ api, bauteil, neuLaden }: {
  api: ApiClient;
  bauteil: LueftungBauteilDetail;
  neuLaden: () => void;
}) {
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const angesehen = async () => {
    setLaeuft(true);
    setFehler(null);
    try {
      await api.befundGeprueft(bauteil.id);
      neuLaden();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <ContentSection
      titel="Befund"
      meta={(
        <>
          <StatusBadge zustand="fehler" text="Fehler" />
          {bauteil.fehlerZustand === 'geprueft' && <> <StatusBadge zustand="neutral" text="angesehen" /></>}
        </>
      )}
      beschreibung={bauteil.fehlerAm
        ? `festgestellt ${zeitpunkt(bauteil.fehlerAm)}${bauteil.fehlerVon ? ` von ${bauteil.fehlerVon}` : ''}`
        : undefined}
    >
      <p>{bauteil.fehlerHinweis}</p>
      {fehler && <ErrorState klein meldung={fehler} />}
      {bauteil.fehlerZustand !== 'geprueft' && (
        <button type="button" className="m-knopf" disabled={laeuft} onClick={() => void angesehen()}>
          Als angesehen kennzeichnen
        </button>
      )}
    </ContentSection>
  );
}
